import {
  PropsWithChildren,
  RefObject,
  useCallback,
  useMemo,
  useState,
} from 'react';
import * as React from 'react';

import { FullScreenModalProps } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useDPFormRecipientTrackerContext } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
// eslint-disable-next-line luminary-rules/no-page-imports -- type-only import needed for navigation coordination between modal and page
import { ManageDispositionPageSections } from '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/ManageDispositionPage.types';

import {
  EditTestamentaryEntitySection,
  getTestamentaryEntitySectionOptions,
} from '../EditTestamentaryEntitySplitScreen.types';
import { useEditTestamentaryEntityInitialValues } from '../hooks/useEditTestamentaryEntityInitialValues';
import {
  EditTestamentaryEntitySplitScreenModalContext,
  SubmitButtonType,
} from './editTestamentaryEntitySplitScreenModal.context';

interface EditTestamentaryEntitySplitScreenModalProviderProps {
  testamentaryEntityId: string;
  isTwoClientHousehold: boolean;
  initialSection: EditTestamentaryEntitySection;
  /**
   * Optional initial value for the first death client ID.
   * Used to pre-select the death order in the dispositions section when opening
   * from a context where we already know which death scenario to display
   * (e.g., from an estate waterfall viewing a specific death order).
   */
  initialFirstDeathClientId?: string | null;
  footerRef: React.RefObject<HTMLDivElement | null>;
  headerRef: React.RefObject<HTMLDivElement | null>;
  duplicatesRefs: Partial<
    Record<AISuggestionsMatcherArrayField, RefObject<HTMLDivElement | null>>
  >;
  onClose: FullScreenModalProps['onClose'];
}

function useContextValue({
  testamentaryEntityId,
  isTwoClientHousehold,
  initialSection,
  initialFirstDeathClientId,
  footerRef,
  headerRef,
  duplicatesRefs,
  onClose,
}: EditTestamentaryEntitySplitScreenModalProviderProps): EditTestamentaryEntitySplitScreenModalContext {
  const { navigate } = useNavigateToRoute();
  const { householdId } = useHouseholdDetailsContext();

  const { handleRequestCloseOuterModal } = useDPFormRecipientTrackerContext();

  const [currentSection, setCurrentSection] =
    useState<EditTestamentaryEntitySection>(initialSection);

  const [clickedSubmitButton, setClickedSubmitButton] =
    useState<SubmitButtonType>(null);

  // Track the selected first death client ID for the dispositions section.
  // Initialized from initialFirstDeathClientId if provided (e.g., from estate waterfall context),
  // and can be changed via the death order selector.
  const [selectedFirstDeathClientId, setSelectedFirstDeathClientId] = useState<
    string | null
  >(initialFirstDeathClientId ?? null);

  const sectionOptions = useMemo(() => {
    return getTestamentaryEntitySectionOptions(isTwoClientHousehold);
  }, [isTwoClientHousehold]);

  const handleCloseModal = useCallback(
    (skipChecks?: boolean) => {
      if (!householdId) return;

      if (skipChecks) {
        onClose?.();
        return;
      }

      handleRequestCloseOuterModal(onClose, () => {
        navigate(
          ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
          {
            householdId,
          },
          {
            section: ManageDispositionPageSections.TESTAMENTARY_ENTITIES,
          }
        );
      });
    },
    [handleRequestCloseOuterModal, householdId, navigate, onClose]
  );

  const handleMissingRecord = useCallback(() => {
    handleCloseModal(true);
  }, [handleCloseModal]);

  const { initialFormValues, loading, hasError } =
    useEditTestamentaryEntityInitialValues({
      testamentaryEntityId,
      onNotFound: handleMissingRecord,
    });

  const isDPSection = useMemo(() => {
    return currentSection === EditTestamentaryEntitySection.DISPOSITIONS;
  }, [currentSection]);

  return {
    testamentaryEntityId,
    isTwoClientHousehold,
    currentSection,
    setCurrentSection,
    isDPSection,
    selectedFirstDeathClientId,
    setSelectedFirstDeathClientId,
    clickedSubmitButton,
    setClickedSubmitButton,
    sectionOptions,
    initialFormValues,
    hasError,
    isLoading: loading,
    footerRef,
    headerRef,
    handleCloseModal,
    duplicatesRefs,
  };
}

export const EditTestamentaryEntitySplitScreenModalProvider = ({
  children,
  ...rest
}: PropsWithChildren<EditTestamentaryEntitySplitScreenModalProviderProps>) => {
  const value = useContextValue(rest);

  return (
    <EditTestamentaryEntitySplitScreenModalContext.Provider value={value}>
      {children}
    </EditTestamentaryEntitySplitScreenModalContext.Provider>
  );
};
